/* TWRD delivery date helper. Shared by the PDP timeline and the cart drawer.
   Rules: cutoff at 14:00 Europe/Copenhagen, production 2 to 3 business days,
   delivery uses country transit data when available, weekends skipped, dates as "5 Sep".
   Country rates are loaded from the theme's delivery rates asset. */
(function () {
  'use strict';

  /* Self locked: the file is loaded from the PDP, the drawer and the cart
     value strip. The second copy must not redefine anything. */
  if (window.TWRDDelivery) return;

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var rateData = null;
  var ratePromise = null;

  /* Current wall clock in Copenhagen, as a plain Date whose fields are local
     to that zone. Uses Intl so DST is handled by the browser. */
  function copenhagenNow() {
    var now = new Date();
    var parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Copenhagen',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    }).formatToParts(now);
    var v = {};
    parts.forEach(function (p) { v[p.type] = p.value; });
    return new Date(
      Number(v.year), Number(v.month) - 1, Number(v.day),
      Number(v.hour) % 24, Number(v.minute), Number(v.second)
    );
  }

  function isWeekend(d) {
    var day = d.getDay();
    return day === 0 || day === 6;
  }

  function addBusinessDays(date, days) {
    var d = new Date(date.getTime());
    var left = days;
    while (left > 0) {
      d.setDate(d.getDate() + 1);
      if (!isWeekend(d)) left--;
    }
    return d;
  }

  /* Move to the next business day if we land on a weekend. */
  function nextBusinessDay(date) {
    var d = new Date(date.getTime());
    while (isWeekend(d)) d.setDate(d.getDate() + 1);
    return d;
  }

  function formatDate(d) {
    return d.getDate() + ' ' + MONTHS[d.getMonth()];
  }

  /* Time left until today's cutoff. Null when the cutoff has passed or today
     is not a business day, because then the order ships the next working day
     and a countdown would be dishonest. */
  function timeToCutoff(cutoffHour, now) {
    var n = now || copenhagenNow();
    if (isWeekend(n)) return null;
    var cutoff = new Date(n.getFullYear(), n.getMonth(), n.getDate(), cutoffHour, 0, 0);
    var ms = cutoff.getTime() - n.getTime();
    return ms > 0 ? ms : null;
  }

  function formatCountdown(ms) {
    var total = Math.floor(ms / 1000);
    var h = Math.floor(total / 3600);
    var m = Math.floor((total % 3600) / 60);
    var s = total % 60;
    function pad(x) { return x < 10 ? '0' + x : String(x); }
    return h > 0 ? h + 'h ' + pad(m) + 'm' : pad(m) + 'm ' + pad(s) + 's';
  }

  function validWindow(min, max) {
    return typeof min === 'number' && isFinite(min) && min >= 0 &&
      typeof max === 'number' && isFinite(max) && max >= min;
  }

  function setRates(data) {
    rateData = data && typeof data === 'object' ? data : null;
    return rateData;
  }

  function loadRates(url) {
    if (rateData) return Promise.resolve(rateData);
    if (ratePromise) return ratePromise;
    ratePromise = window.fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Delivery rates request failed: ' + response.status);
        return response.json();
      })
      .then(setRates)
      .catch(function (err) {
        ratePromise = null;
        throw err;
      });
    return ratePromise;
  }

  function countryCode(fallback) {
    try {
      if (window.Shopify && typeof window.Shopify.country === 'string') {
        var shopifyCountry = window.Shopify.country.trim().toUpperCase();
        if (/^[A-Z]{2}$/.test(shopifyCountry)) return shopifyCountry;
      }
    } catch (e) {}

    var input = document.querySelector('input[name="country_code"][value]');
    if (input) {
      var inputCountry = (input.value || '').trim().toUpperCase();
      if (/^[A-Z]{2}$/.test(inputCountry)) return inputCountry;
    }

    var supplied = String(fallback || '').trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(supplied)) return supplied;

    var lang = (document.documentElement.getAttribute('lang') || '').trim().split('-');
    var region = lang.length > 1 ? lang[lang.length - 1].toUpperCase() : '';
    return /^[A-Z]{2}$/.test(region) ? region : '';
  }

  function transitWindow(code, fallbackMin, fallbackMax) {
    if (rateData && Array.isArray(rateData.countries)) {
      var country = rateData.countries.find(function (item) { return item.code === code; });
      if (country && validWindow(country.transit_min, country.transit_max)) {
        return [country.transit_min, country.transit_max];
      }
      var zone = country && rateData.zones ? rateData.zones[country.zone] : null;
      if (zone && validWindow(zone.transit_min, zone.transit_max)) {
        return [zone.transit_min, zone.transit_max];
      }
    }
    return [fallbackMin, fallbackMax];
  }

  /* Full schedule. shipMin/shipMax stay as backwards-compatible fallbacks. */
  function schedule(opts) {
    var o = opts || {};
    var cutoffHour = o.cutoffHour == null ? 14 : o.cutoffHour;
    var prodMin = o.prodMin == null ? 2 : o.prodMin;
    var prodMax = o.prodMax == null ? 3 : o.prodMax;
    var fallbackMin = o.transitMin == null ? (o.shipMin == null ? 2 : o.shipMin) : o.transitMin;
    var fallbackMax = o.transitMax == null ? (o.shipMax == null ? 4 : o.shipMax) : o.transitMax;
    var transit = transitWindow(countryCode(o.countryCode), fallbackMin, fallbackMax);
    var now = o.now || copenhagenNow();

    var beforeCutoff = timeToCutoff(cutoffHour, now) !== null;
    /* Orders after cutoff, or on a weekend, start on the next business day. */
    var start = beforeCutoff ? nextBusinessDay(now) : nextBusinessDay(addBusinessDays(now, 1));

    var shipFrom = addBusinessDays(start, prodMin);
    var shipTo = addBusinessDays(start, prodMax);
    var deliverFrom = addBusinessDays(shipFrom, transit[0]);
    var deliverTo = addBusinessDays(shipTo, transit[1]);

    return {
      now: now,
      beforeCutoff: beforeCutoff,
      msToCutoff: timeToCutoff(cutoffHour, now),
      ordered: start,
      shipFrom: shipFrom,
      shipTo: shipTo,
      deliverFrom: deliverFrom,
      deliverTo: deliverTo,
      orderedText: formatDate(start),
      shippedText: formatDate(shipFrom) + ' – ' + formatDate(shipTo),
      deliveredText: formatDate(deliverFrom) + ' – ' + formatDate(deliverTo)
    };
  }

  window.TWRDDelivery = {
    now: copenhagenNow,
    schedule: schedule,
    setRates: setRates,
    loadRates: loadRates,
    formatDate: formatDate,
    formatCountdown: formatCountdown,
    addBusinessDays: addBusinessDays,
    isWeekend: isWeekend
  };
})();
